import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { MaskText } from "../components/elysium/primitives";

const FIELD =
  "w-full rounded-md border border-ely-border bg-ely-bg/60 px-4 py-3.5 font-sans text-base text-ely-text placeholder:text-ely-muted/50 outline-none transition-colors duration-300 focus:border-ely-primary";

export const AuthPage = ({ mode }) => {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        await register(form.name, form.email, form.password);
      } else {
        await login(form.email, form.password);
      }
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ely-bg px-6 py-20">
      <div className="grain" aria-hidden="true" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-ely-primary/[0.05] blur-3xl" />

      <Link
        to="/"
        data-testid="auth-back-home"
        className="absolute left-6 top-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ely-muted transition-colors hover:text-ely-primary md:left-12 md:top-10"
      >
        <ArrowLeft size={14} /> Home
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="font-serif text-3xl tracking-wide text-ely-text">Soul-Key</span>
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-ely-primary" />
          </Link>
        </div>

        <div className="rounded-xl border border-ely-border/60 bg-ely-surface/60 p-8 backdrop-blur-xl md:p-10">
          <h1 className="font-serif text-4xl font-light leading-none text-ely-text">
            <MaskText lines={[isRegister ? "Begin your legacy." : "Welcome back."]} />
          </h1>
          <p className="mt-4 font-sans text-sm font-light text-ely-muted">
            {isRegister
              ? "Create your account to start preserving your voice, stories, and wisdom."
              : "Sign in to continue building and revisiting your legacy."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {isRegister && (
              <input
                type="text"
                required
                placeholder="Your name"
                className={FIELD}
                value={form.name}
                onChange={handle("name")}
                data-testid="auth-name-input"
              />
            )}
            <input
              type="email"
              required
              placeholder="Email address"
              className={FIELD}
              value={form.email}
              onChange={handle("email")}
              data-testid="auth-email-input"
            />
            <input
              type="password"
              required
              placeholder="Password"
              className={FIELD}
              value={form.password}
              onChange={handle("password")}
              data-testid="auth-password-input"
            />

            {error && (
              <p className="font-sans text-sm text-red-400" data-testid="auth-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-ely-primary px-6 py-3.5 font-sans text-sm font-medium text-ely-bg transition-all duration-500 hover:bg-ely-primaryHover disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {isRegister ? "Create Account" : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-center font-sans text-sm font-light text-ely-muted">
            {isRegister ? "Already have an account? " : "New to Soul-Key? "}
            <Link
              to={isRegister ? "/login" : "/register"}
              className="text-ely-primary underline-offset-4 hover:underline"
              data-testid="auth-switch-mode"
            >
              {isRegister ? "Sign in" : "Create one"}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};
